import request from 'lib/request';

export interface Response {
  code: number;
  data: RaffleDetails;
  msg: string;
}

export interface RaffleDetails {
  project_other_active_raffles: ProjectOtherActiveRaffle[];
  raffle: Raffle;
  /**
   * 当前项目下已中奖的mint wallets
   */
  has_won_mint_wallets: string[];
  you_won_raffles: YouWonRaffle[];
  is_owner: number;
  is_community_team_member: number;
}

export interface ProjectOtherActiveRaffle {
  campaign: Campaign;
  /**
   * 参与抽奖人数
   */
  entry_count: string;
  is_hide_community_logo: boolean;
  is_hide_project_logo: boolean;
  my_entry: MyEntry;
  /**
   * raffle 名字
   */
  name?: string;
  role_based_dc_server_name: string;
  /**
   * url唯一标识
   */
  vanity_url?: string;
}

export interface Campaign {
  id: number;
  /**
   * 推特链接
   */
  twitter_link?: string;
  /**
   * logo
   */
  logo: string;
  /**
   * 名称
   */
  name: string;
}

export interface MyEntry {
  /**
   * 创建时间
   */
  create_time: string;
  /**
   * dc avatar
   */
  discord_avatar: string;
  /**
   * dc id
   */
  discord_id: string;
  /**
   * dc username
   */
  discord_username: string;
  /**
   * 物理主键
   */
  id: number;
  /**
   * 是否中奖
   */
  is_win: number;
  /**
   * mint wallet
   */
  mint_wallet: string;
  /**
   * 权重
   */
  multiplier: number;
  /**
   * tw user id
   */
  twitter_user_id: string;
  /**
   * tw username
   */
  twitter_username: string;
  /**
   * 更新时间
   */
  update_time: string;
  email?: string;
  award_code?: string;
}

/**
 * raffle参与用户
 */
export interface RaffleEntries {
  /**
   * 当前参与人数
   */
  current_entries_count: number;
}

export interface Raffle {
  campaign: Campaign;
  /**
   * 创建时间
   */
  create_time?: number;
  /**
   * 剩余时间（秒）
   */
  remaining_seconds: number;
  /**
   * 描述
   */
  description?: string;
  /**
   * 结束时间
   */
  end_time?: number;
  /**
   * 物理主键
   */
  id?: number;
  /**
   * 是否隐藏参与人数
   */
  is_hide_entry_count?: number;
  /**
   * 该raffle是否已发送dc new raffle消息
   */
  is_dc_new_raffle_announced: boolean;
  is_hide_project_logo: boolean;
  is_hide_community_logo: boolean;
  /**
   * more_info_for_winners
   */
  more_info_for_winners?: string;
  my_entry?: MyEntry;
  /**
   * raffle 名字
   */
  name?: string;
  project?: Project;
  /**
   * raffle参与用户
   */
  entry_count: number;
  /**
   * dc requirement开关是否打开
   */
  requirement_is_dc_open?: number;
  /**
   * tw requirement开关是否打开
   */
  requirement_is_tw_open?: number;
  /**
   * wallet balance requirement开关是否打开
   */
  requirement_is_wallet_balance_open?: number;
  /**
   * wallet nft requirement开关是否打开
   */
  requirement_is_wallet_nft_open?: number;
  /**
   * allow list requirement开关是否打开
   */
  requirement_is_wallet_allow_list_open: boolean;
  /**
   * email requirement开关是否打开
   */
  requirement_is_email_open?: boolean;
  requirements?: RaffleRequirement[];
  /**
   * whitelist method为DC_ROLE_BASED时的dc invite link
   */
  role_based_dc_invite_link?: string;
  role_based_dc_server_name?: string;
  /**
   * 开始时间
   */
  start_time?: number;
  /**
   * 状态: NOT_START, ACTIVE, ENDED, FINALIZED
   */
  status?: string;
  /**
   * raffle 类型：RAFFLE、FCFS、WALLET_COLLECTION
   */
  type?: string;
  /**
   * url唯一标识
   */
  vanity_url?: string;
  /**
   * DTC、DC_ROLE_BASED、OTHER
   */
  whitelist_method?: string;
  /**
   * 中奖人数
   */
  winner_count?: number;
  owner: Owner;
}

export interface Project {
  /**
   * 项目banner
   */
  banner: string;
  /**
   * blockchain
   */
  blockchain?: string;
  /**
   * discord邀请链接
   */
  dc_invite_link?: string;
  /**
   * 物理主键
   */
  id?: number;
  /**
   * 是否推荐
   */
  is_recommend: boolean;
  /**
   * 是否追踪该项目
   */
  is_tracked: boolean;
  logo: string;
  /**
   * mint时间
   */
  mint_date?: number;
  /**
   * mint时间
   */
  mint_time?: string;
  /**
   * 项目名称
   */
  name?: string;
  /**
   * public price
   */
  public_price?: string;
  /**
   * supply count
   */
  supply_count?: string;
  /**
   * 追踪人数
   */
  track_amount?: number;
  /**
   * 推特链接
   */
  twitter_link?: string;
  twitter_screen_name?: string;
  /**
   * website
   */
  website?: string;
  /**
   * whitelist count
   */
  whitelist_count?: string;
  /**
   * whitelist price
   */
  whitelist_price?: string;
}

/**
 * raffle_requirement
 */
export interface RaffleRequirement {
  /**
   * 具体的配置
   */
  config?: RequirementConfig;
  /**
   * 创建时间
   */
  create_time: number;
  /**
   * 物理主键
   */
  id: number;
  /**
   * requirement的类型：TWITTER、DISCORD、WALLET_BALANCE、WALLET_NFT
   */
  type: string;
  /**
   * 更新时间
   */
  update_time: number;
}

/**
 * 具体的配置
 *
 * twitter requirement config
 *
 * discord requirement config
 *
 * wallet balance requirement config
 *
 * wallet nft requirement config
 */
export interface RequirementConfig {
  follow_users?: TwitterUser[];
  /**
   * 要求retweet和like的tweet id
   */
  retweet_and_like?: string;
  /**
   * 服务器信息
   */
  /**
   * 要求的eth余额
   */
  eth?: string;
  /**
   * opensea链接列表
   */
  nft_collections?: NFTCollection[];
  /**
   * 是否必要，值：required | optional
   */
  necessary?: string;
  /**
   * wallet whitelist name
   */
  name?: string;
}

export interface DiscordConfig {
  guild?: Guild;
  /**
   * 角色列表
   */
  roles?: Role[];
}

/**
 * twitter user
 */
export interface TwitterUser {
  id: string;
  /**
   * 头像
   */
  logo: string;
  /**
   * 账号名
   */
  screen_name: string;
  /**
   * 昵称
   */
  username: string;
}

/**
 * 服务器信息
 */
export interface Guild {
  id: string;
  invite_link: string;
  server_name: string;
}

export interface NFTCollection {
  /**
   * nft地址
   */
  address: string;
  /**
   * nft image
   */
  logo: string;
  /**
   * nft名字
   */
  name: string;
  link: string;
  weight: Weight;
}

export interface Role {
  /**
   * 角色id
   */
  id: string;
  /**
   * 角色名称
   */
  name: string;
  // 权重
  multiplier?: number;
  /**
   * 是否叠加
   */
  is_stacking?: string;
}

export interface Weight {
  /**
   * 权重
   */
  multiplier?: number;
  /**
   * 值固定为：PER_NFT
   *
   * 值固定为：TIERED_NUM，表示后台的advanced
   */
  type: string;
  gradients?: Gradient[];
  default_multiplier?: number;
  non_holder?: boolean;
}

export interface Gradient {
  multiplier: number;
  threshold: number;
}

export interface YouWonRaffle {
  /**
   * 社区详情
   */
  campaign: Campaign;
  is_hide_community_logo: boolean;
  is_hide_project_logo: boolean;
  /**
   * 我的参与记录详情
   */
  my_entry: MyEntry;
  /**
   * raffle 名字
   */
  name?: string;
  owner: Owner;
  /**
   * 项目详情
   */
  project: YouWonRaffleProject;
  /**
   * url唯一标识
   */
  vanity_url?: string;
}

export interface Owner {
  address: string;
  discord_avatar: string;
  discord_username: string;
  twitter_avatar: string;
  twitter_username: string;
  /**
   * twitter 写权限
   */
  twitter_write_permission: boolean;
}

/**
 * 项目详情
 *
 * project
 */
export interface YouWonRaffleProject {
  /**
   * 项目banner
   */
  banner: string;
  /**
   * blockchain
   */
  chain?: string;
  /**
   * 创建时间
   */
  create_time?: Date;
  /**
   * discord服务器id
   */
  dc_guild_id?: string;
  /**
   * discord邀请链接
   */
  dc_invite_link?: string;
  /**
   * 物理主键
   */
  id?: number;
  logo: string;
  /**
   * mint时间
   */
  mint_date?: string;
  /**
   * 项目名称
   */
  name?: string;
  /**
   * public price
   */
  public_price?: string;
  /**
   * supply count
   */
  supply_count?: string;
  /**
   * 推特链接
   */
  twitter_link?: string;
  /**
   * twitter_user_id
   */
  twitter_user_id?: string;
  /**
   * 更新时间
   */
  update_time?: Date;
  /**
   * website
   */
  website?: string;
  /**
   * whitelist count
   */
  whitelist_count?: string;
  /**
   * whitelist price
   */
  whitelist_price?: string;
}

export async function getRaffleDetails(vanity_url: string, auth?: string) {
  const res = await request<Response>('/raffle/detail', {
    headers: {
      [process.env.NEXT_PUBLIC_AUTHORIZATION_KEY]: auth || '',
    },
    params: {
      vanity_url,
    },
  });
  return res?.data;
}

export async function refreshRaffleDetails(vanity_url: string) {
  const res = await request<Response>('/raffle/detail', {
    params: {
      vanity_url,
    },
  });
  return res?.data;
}
